import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Snackbar,
  Alert,
  Stack,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Grid,
  TablePagination,
  TableContainer,
  CircularProgress,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import Header from "../components/Header";
import axios from "axios";

import { useTranslation } from "react-i18next";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

export default function SubscriptionManagementPage() {
  const [tab, setTab] = useState(0);

  // PLANS
  const [plans, setPlans] = useState([]);
  const [planDialog, setPlanDialog] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [planForm, setPlanForm] = useState({
    tier: "",
    unitLimit: 0,
    features: [],
    description: "",
  });
  // Plans pagination
  const [planPage, setPlanPage] = useState(0);
  const [planRowsPerPage, setPlanRowsPerPage] = useState(5);
  const { i18n } = useTranslation();

  // SUBSCRIPTIONS
  const [subs, setSubs] = useState([]);
  const [subsDialog, setSubsDialog] = useState(false);
  const [editingSub, setEditingSub] = useState(null);
  const [subForm, setSubForm] = useState({
    compoundId: "",
    planId: "",
    startDate: "",
    endDate: "",
  });
  const [compounds, setCompounds] = useState([]);
  // Subs pagination
  const [subsPage, setSubsPage] = useState(0);
  const [subsRowsPerPage, setSubsRowsPerPage] = useState(10);

  // Over-limit compounds
  const [overLimitCompounds, setOverLimitCompounds] = useState([]);

  const [isLoading, setIsLoading] = useState(false);
  // Notifications
  const [snack, setSnack] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  // --- Load Data ---
  useEffect(() => {
    fetchPlans();
    fetchSubs();
    fetchCompounds();
    fetchOverLimitCompounds();
  }, []);

  const fetchPlans = async () => {
    try {
      setIsLoading(true);
      const res = await axios.get(`${API_BASE}/api/admin/subscription-plans`);
      setPlans(res.data.data || []);
    } catch (error) {
      setSnack({
        open: true,
        message: "Sorry! Can't fetch the data. Please try again.",
        severity: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };
  const fetchSubs = async () => {
    try {
      setIsLoading(true);
      const res = await axios.get(`${API_BASE}/api/admin/subscriptions`);
      setSubs(res.data.data || []);
    } catch (error) {
      setSnack({
        open: true,
        message: "Sorry! Can't fetch the data. Please try again.",
        severity: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };
  const fetchCompounds = async () => {
    try {
      setIsLoading(true);
      const res = await axios.get(`${API_BASE}/api/admin/getCompoundList`);
      setCompounds(res.data.compounds || []);
    } catch (error) {
      setSnack({
        open: true,
        message: "Sorry! Can't fetch the data. Please try again.",
        severity: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };
  const fetchOverLimitCompounds = async () => {
    try {
      setIsLoading(true);
      const res = await axios.get(`${API_BASE}/api/admin/compounds-over-limit`);
      setOverLimitCompounds(res.data.data || []);
    } catch (error) {
      setSnack({
        open: true,
        message: "Sorry! Can't fetch the data. Please try again.",
        severity: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // --- Plan Dialog ---
  const openPlanDialog = (plan) => {
    setEditingPlan(plan);
    if (plan) setPlanForm(plan);
    else setPlanForm({ tier: "", unitLimit: 0, features: [], description: "" });
    setPlanDialog(true);
  };

  const handlePlanSave = async () => {
    try {
      if (!planForm.tier || !planForm.unitLimit)
        throw new Error("Fill all required fields.");
      if (editingPlan) {
        await axios.put(
          `${API_BASE}/api/admin/subscription-plans/${editingPlan.id}`,
          planForm
        );
        setSnack({ open: true, message: "Plan updated", severity: "success" });
      } else {
        await axios.post(`${API_BASE}/api/admin/subscription-plans`, planForm);
        setSnack({ open: true, message: "Plan created", severity: "success" });
      }
      setPlanDialog(false);
      fetchPlans();
    } catch (e) {
      setSnack({ open: true, message: e.message, severity: "error" });
    }
  };
  const handlePlanDelete = async (plan) => {
    if (!window.confirm(`Delete plan ${plan.tier}?`)) return;
    try {
      await axios.delete(`${API_BASE}/api/admin/subscription-plans/${plan.id}`);
      setSnack({ open: true, message: "Plan deleted", severity: "success" });
      fetchPlans();
    } catch (e) {
      setSnack({ open: true, message: e.message, severity: "error" });
    }
  };

  // --- Subscription Dialog ---
  const openSubDialog = (sub) => {
    setEditingSub(sub);
    if (sub)
      setSubForm({
        compoundId: sub.compoundId,
        planId: sub.planId,
        startDate: sub.startDate?.slice(0, 10) || "",
        endDate: sub.endDate?.slice(0, 10) || "",
      });
    else setSubForm({ compoundId: "", planId: "", startDate: "", endDate: "" });
    setSubsDialog(true);
  };
  const handleSubSave = async () => {
    try {
      if (!subForm.compoundId || !subForm.planId)
        throw new Error("Fill all required fields.");
      if (editingSub) {
        await axios.put(
          `${API_BASE}/api/admin/subscriptions/${editingSub.id}`,
          subForm
        );
        setSnack({
          open: true,
          message: "Subscription updated",
          severity: "success",
        });
      } else {
        await axios.post(`${API_BASE}/api/admin/subscriptions`, subForm);
        setSnack({
          open: true,
          message: "Subscription assigned",
          severity: "success",
        });
      }
      setSubsDialog(false);
      fetchSubs();
      fetchOverLimitCompounds(); // Refresh after changes
    } catch (e) {
      setSnack({ open: true, message: e.message, severity: "error" });
    }
  };
  const handleSubDelete = async (sub) => {
    if (!window.confirm(`Remove subscription for ${sub.compound.name}?`))
      return;
    try {
      await axios.delete(`${API_BASE}/api/admin/subscriptions/${sub.id}`);
      setSnack({
        open: true,
        message: "Subscription deleted",
        severity: "success",
      });
      fetchSubs();
      fetchOverLimitCompounds();
    } catch (e) {
      setSnack({ open: true, message: e.message, severity: "error" });
    }
  };

  // --- Main Render ---
  return (
    <Box className="main-container mt-4">
      <Header title="System Settings" />
      {isLoading ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="80vh"
        >
          <CircularProgress color="success" />
        </Box>
      ) : (
        <Grid container columns={{ xs: 12, sm: 12, md: 12 }} spacing={3}>
          <Grid item size={{ xs: 12, sm: 6, md: 5 }}>
            <Grid container columns={{ xs: 12, sm: 12, md: 12 }} spacing={3}>
              <Grid item size={{ xs: 12, sm: 12, md: 12 }}>
                <Paper
                  sx={{
                    border: "1px",
                    minHeight: "35vh",
                    p: 1,
                    borderBlockEndColor: "#cbd5e1",
                  }}
                  elevation={3}
                >
                  <Box display="flex" justifyContent="flex-end" mb={2}>
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={() => openPlanDialog(null)}
                    >
                      Add Plan
                    </Button>
                  </Box>
                  <TableContainer
                    boxShadow={3}
                    component={Box}
                    sx={{
                      maxHeight: "30vh",
                      overflow: "auto", // enable both horizontal and vertical
                      width: "100%", // ensure it spans available space
                      scrollbarWidth: "thin",
                      "&::-webkit-scrollbar": {
                        height: "8px", // 👈 ensures horizontal bar shows
                        width: "8px",
                      },
                      "&::-webkit-scrollbar-track": {
                        backgroundColor: "#f0f0f0",
                        borderRadius: "8px",
                      },
                      "&::-webkit-scrollbar-thumb": {
                        backgroundColor: "#c1c1c1",
                        borderRadius: "8px",
                        border: "2px solid #f0f0f0",
                      },
                      "&::-webkit-scrollbar-thumb:hover": {
                        backgroundColor: "#a0a0a0",
                      },
                    }}
                  >
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>Tier</TableCell>
                          <TableCell>Unit Limit</TableCell>
                          <TableCell>Description</TableCell>
                          <TableCell>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {plans
                          .slice(
                            planPage * planRowsPerPage,
                            planPage * planRowsPerPage + planRowsPerPage
                          )
                          .map((plan) => (
                            <TableRow key={plan.id}>
                              <TableCell>{plan.tier}</TableCell>
                              <TableCell>{plan.unitLimit}</TableCell>
                              <TableCell>{plan.description}</TableCell>
                              <TableCell>
                                <IconButton
                                  onClick={() => openPlanDialog(plan)}
                                >
                                  <EditIcon />
                                </IconButton>
                                <IconButton
                                  color="error"
                                  onClick={() => handlePlanDelete(plan)}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <TablePagination
                    component="div"
                    count={plans.length}
                    page={planPage}
                    onPageChange={(e, newPage) => setPlanPage(newPage)}
                    rowsPerPage={planRowsPerPage}
                    onRowsPerPageChange={(e) => {
                      setPlanRowsPerPage(parseInt(e.target.value, 10));
                      setPlanPage(0);
                    }}
                    rowsPerPageOptions={[5, 10, 20]}
                  />
                </Paper>
              </Grid>
              <Grid item size={{ xs: 12, sm: 12, md: 12 }}>
                <Paper
                  sx={{
                    border: "1px",
                    minHeight: "35vh",
                    p: 1,
                    borderBlockEndColor: "#cbd5e1",
                  }}
                  elevation={3}
                >
                  <Typography variant="h6" color="error" sx={{ mb: 2 }}>
                    Compounds Over Their Subscription Unit Limit
                  </Typography>
                  {overLimitCompounds.length === 0 ? (
                    <Typography>No compounds are over their limit.</Typography>
                  ) : (
                    <TableContainer sx={{ boxShadow: 3 }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell>Compound</TableCell>
                            <TableCell>Plan</TableCell>
                            <TableCell>Unit Limit</TableCell>
                            <TableCell>Current Units</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {overLimitCompounds.map((c) => (
                            <TableRow key={c.id}>
                              <TableCell className="notranslate">
                                {c.name}
                              </TableCell>
                              <TableCell>{c.plan?.tier}</TableCell>
                              <TableCell>{c.plan?.unitLimit}</TableCell>
                              <TableCell>{c.unitCount}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </Paper>
              </Grid>
            </Grid>
          </Grid>

          <Grid item size={{ xs: 12, sm: 6, md: 7 }}>
            <Paper
              sx={{
                border: "1px",
                minHeight: "80vh",
                p: 1,
                borderBlockEndColor: "#cbd5e1",
              }}
              elevation={3}
            >
              <Box display="flex" justifyContent="flex-end" mb={2}>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => openSubDialog(null)}
                >
                  Assign Plan
                </Button>
              </Box>
              <TableContainer
                boxShadow={3}
                component={Box}
                sx={{
                  maxHeight: "75vh",
                  overflow: "auto", // enable both horizontal and vertical
                  width: "100%", // ensure it spans available space
                  scrollbarWidth: "thin",
                  "&::-webkit-scrollbar": {
                    height: "8px", // 👈 ensures horizontal bar shows
                    width: "8px",
                  },
                  "&::-webkit-scrollbar-track": {
                    backgroundColor: "#f0f0f0",
                    borderRadius: "8px",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    backgroundColor: "#c1c1c1",
                    borderRadius: "8px",
                    border: "2px solid #f0f0f0",
                  },
                  "&::-webkit-scrollbar-thumb:hover": {
                    backgroundColor: "#a0a0a0",
                  },
                }}
              >
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Compound</TableCell>
                      <TableCell>Plan</TableCell>
                      <TableCell>Start Date</TableCell>
                      <TableCell>End Date</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {subs
                      .slice(
                        subsPage * subsRowsPerPage,
                        subsPage * subsRowsPerPage + subsRowsPerPage
                      )
                      .map((sub) => (
                        <TableRow key={sub.id}>
                          <TableCell className="notranslate">
                            {sub.compound?.name}
                          </TableCell>
                          <TableCell>{sub.plan?.tier}</TableCell>
                          <TableCell>
                            {sub.startDate && sub.startDate.slice(0, 10)}
                          </TableCell>
                          <TableCell>
                            {sub.endDate && sub.endDate.slice(0, 10)}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={sub.isActive ? "Active" : "Inactive"}
                              color={sub.isActive ? "success" : "error"}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            <IconButton onClick={() => openSubDialog(sub)}>
                              <EditIcon />
                            </IconButton>
                            <IconButton
                              color="error"
                              onClick={() => handleSubDelete(sub)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={subs.length}
                page={subsPage}
                onPageChange={(e, newPage) => setSubsPage(newPage)}
                rowsPerPage={subsRowsPerPage}
                onRowsPerPageChange={(e) => {
                  setSubsRowsPerPage(parseInt(e.target.value, 10));
                  setSubsPage(0);
                }}
                rowsPerPageOptions={[5, 10, 20]}
              />
            </Paper>

            {/* Over-limit Compounds */}
          </Grid>
        </Grid>
      )}

      {/* PLAN DIALOG */}
      <Dialog
        open={planDialog}
        onClose={() => setPlanDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{editingPlan ? "Edit Plan" : "Add Plan"}</DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <TextField
              label="Tier"
              value={planForm.tier}
              onChange={(e) =>
                setPlanForm({ ...planForm, tier: e.target.value })
              }
              fullWidth
            />
            <TextField
              label="Unit Limit"
              type="number"
              value={planForm.unitLimit}
              onChange={(e) =>
                setPlanForm({
                  ...planForm,
                  unitLimit: Number(e.target.value),
                })
              }
              fullWidth
            />
            <TextField
              label="Description"
              value={planForm.description || ""}
              onChange={(e) =>
                setPlanForm({ ...planForm, description: e.target.value })
              }
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPlanDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handlePlanSave}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* SUBSCRIPTION DIALOG */}
      <Dialog
        open={subsDialog}
        onClose={() => setSubsDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingSub ? "Edit Assignment" : "Assign Plan to Compound"}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <FormControl fullWidth>
              <InputLabel>Compound</InputLabel>
              <Select
                value={subForm.compoundId}
                onChange={(e) =>
                  setSubForm({ ...subForm, compoundId: e.target.value })
                }
                className="notranslate"
                label="Compound"
              >
                {compounds.map((cmp) => (
                  <MenuItem value={cmp.id} key={cmp.id} className="notranslate">
                    {cmp.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Plan</InputLabel>
              <Select
                value={subForm.planId}
                onChange={(e) =>
                  setSubForm({ ...subForm, planId: e.target.value })
                }
                label="Plan"
              >
                {plans.map((pl) => (
                  <MenuItem value={pl.id} key={pl.id}>
                    {pl.tier}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <LocalizationProvider
              dateAdapter={AdapterDayjs}
              adapterLocale={i18n.language === "ar" ? "ar" : "en"} // Change dynamically
            >
              <DatePicker
                className="notranslate"
                label={i18n.language === "ar" ? "تاريخ البدء" : "Start Date"}
                value={subForm.startDate ? dayjs(subForm.startDate) : null}
                onChange={(newValue) =>
                  setSubForm({
                    ...subForm,
                    startDate: newValue ? newValue.format("YYYY-MM-DD") : "",
                  })
                }
                slotProps={{
                  textField: {
                    placeholder: "", // ✅ Hide placeholder
                    fullWidth: true,
                  },
                }}
              />
            </LocalizationProvider>
            <LocalizationProvider
              dateAdapter={AdapterDayjs}
              adapterLocale={i18n.language === "ar" ? "ar" : "en"} // Change dynamically
            >
              <DatePicker
                className="notranslate"
                label={i18n.language === "ar" ? "تاريخ الانتهاء" : "End Date"}
                value={subForm.endDate ? dayjs(subForm.endDate) : null}
                onChange={
                  (newValue) =>
                    setSubForm({
                      ...subForm,
                      endDate: newValue ? newValue.format("YYYY-MM-DD") : "",
                    })
                  // setEndDate(newValue ? newValue.format("YYYY-MM-DD") : "")
                }
                slotProps={{
                  textField: {
                    placeholder: "", // ✅ Hide placeholder
                    fullWidth: true,
                  },
                }}
              />
            </LocalizationProvider>
            {/* <TextField
              label="Start Date"
              type="date"
              value={subForm.startDate}
              onChange={(e) =>
                setSubForm({ ...subForm, startDate: e.target.value })
              }
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <TextField
              label="End Date"
              type="date"
              value={subForm.endDate}
              onChange={(e) =>
                setSubForm({ ...subForm, endDate: e.target.value })
              }
              InputLabelProps={{ shrink: true }}
              fullWidth
            /> */}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSubsDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSubSave}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* NOTIFICATION SNACKBAR */}
      <Snackbar
        open={snack.open}
        autoHideDuration={2500}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        onClose={() => setSnack({ ...snack, open: false })}
      >
        <Alert severity={snack.severity}>{snack.message}</Alert>
      </Snackbar>
    </Box>
  );
}
